// shell/SignupCtaBar.tsx — fixed bottom conversion bar for signed-out visitors.
//
// Rendered by ShellLayout on every shell page when !isAuthenticated. Plain, no-hype
// copy (product rule: personalization/growth surfaces stay honest, not manipulative —
// see the README's free-speech-first framing). No dismiss control: this IS the
// conversion surface for a guest, not a transient notice like VerifyEmailBanner.
//
// On viewports where BottomTabBar renders (mobile), this bar sits directly ABOVE it
// (bottom offset = the tab bar's own reserved height); everywhere else it sits at the
// true viewport bottom. It is `position: fixed` and spans the FULL viewport width, so
// it visually overlaps the left Rail and right RightRail columns on desktop/tablet too
// — both of those reserve SIGNUP_CTA_BAR_HEIGHT of their own bottom padding for exactly
// this reason (see Rail.tsx, RightRail.tsx). ShellLayout's Center/MobileCenter do the
// same for the feed itself.
//
// Fix-round item 15 (1.21.60 adversarial review): at mobile widths the single-row
// layout does not fit (long copy + two buttons in ~343px), so it forces a predictable
// STACKED two-row layout instead of relying on flex-wrap's reflow — an unreserved wrap
// was hiding ~30-40px of the last feed card under the bar because the reserved height
// (SIGNUP_CTA_BAR_HEIGHT) assumed a single row. MOBILE_SIGNUP_CTA_BAR_HEIGHT reserves
// the real (deterministic, not measured) height of that stacked layout instead.
//
// Fix-round item 20 (KNOWN-MINOR DEFERRAL, not fixed this round): a guest's Popover
// (e.g. PostCard's Share menu) opened on a card near the viewport bottom can render
// UNDER this bar. Popover's Panel (z-index 50, position: absolute, no portal) is a
// descendant of ShellLayout's Center, which has `isolation: isolate` — a stacking
// context boundary that caps EVERY z-index inside it (including Popover's 50) below
// this bar's z-index (45), a SIBLING of Center's ancestor Grid (z-index 1), regardless
// of Popover's own z-index value. Two real fixes exist and both carry more risk than
// is warranted in this pass: (a) portal Popover to document.body like Sheet/Dialog/Toast
// already are — Popover is shared by many call sites (PostCard share/···, FeedTabs'
// topic menu, comment menus, ...), each with its own positioning assumptions and tests;
// (b) remove Center's `isolation: isolate` — deliberately added for the Sundown Signal
// backdrop-blend design, not for stacking, so touching it risks a visual regression
// unrelated to this slice. Simply lowering THIS bar's z-index below Grid's would fix
// the popover case but break the bar's actual job (staying visible above feed cards
// that scroll under it) — not a real fix. Left as a follow-up.

import { Link, useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useViewport } from '@/hooks';

/** Reserved height (px, excluding the safe-area inset) on desktop/tablet (single-row
 * layout) — Rail/RightRail/Center all add this to their bottom padding for a guest so
 * the bar never covers anything underneath it. */
export const SIGNUP_CTA_BAR_HEIGHT = 64;

/** Reserved height (px, excluding the safe-area inset) on mobile, where the bar forces
 * a stacked two-row layout (see the file docstring) — taller and deterministic, not
 * measured from the rendered DOM. */
export const MOBILE_SIGNUP_CTA_BAR_HEIGHT = 116;

/** BottomTabBar's own reserved height (see shell/BottomTabBar/BottomTabBar.tsx's `Nav`). */
const TAB_BAR_HEIGHT = 56;

const Bar = styled.div<{ $aboveTabBar: boolean; $stacked: boolean }>`
  position: fixed;
  left: 0;
  right: 0;
  bottom: ${({ $aboveTabBar }) =>
    $aboveTabBar ? `calc(${TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom))` : '0'};
  z-index: 45;
  display: flex;
  flex-direction: ${({ $stacked }) => ($stacked ? 'column' : 'row')};
  align-items: ${({ $stacked }) => ($stacked ? 'stretch' : 'center')};
  justify-content: ${({ $stacked }) => ($stacked ? 'center' : 'space-between')};
  gap: ${({ $stacked }) => ($stacked ? '8px' : '12px')};
  min-height: ${({ $stacked }) => ($stacked ? MOBILE_SIGNUP_CTA_BAR_HEIGHT : SIGNUP_CTA_BAR_HEIGHT)}px;
  padding: 10px 16px;
  padding-bottom: ${({ $aboveTabBar }) => ($aboveTabBar ? '10px' : 'calc(10px + env(safe-area-inset-bottom))')};
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const Text = styled.p<{ $stacked: boolean }>`
  margin: 0;
  ${({ $stacked }) => ($stacked ? 'text-align: center;' : 'flex: 1 1 220px; min-width: 0;')}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Actions = styled.div<{ $stacked: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  ${({ $stacked }) =>
    $stacked &&
    css`
      width: 100%;

      & > a {
        flex: 1;
      }
    `}
`;

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform 80ms ease, opacity 160ms ease;
`;

const PrimaryLink = styled(Link)`
  ${buttonBase}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};

  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const SecondaryLink = styled(Link)`
  ${buttonBase}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};

  &:active {
    transform: translateY(1px);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

export function SignupCtaBar() {
  const viewport = useViewport();
  const location = useLocation();
  const aboveTabBar = viewport === 'mobile';
  // Fix-round item 15: mobile forces the stacked layout unconditionally — see docstring.
  const stacked = viewport === 'mobile';
  // Fix-round item 24: BOTH links carry ?next= back to the current route — Create
  // account is the PRIMARY action (the one most guests should land back on their
  // page from after signing up), so it needs the same return path Sign in already had.
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <Bar
      $aboveTabBar={aboveTabBar}
      $stacked={stacked}
      role="region"
      aria-label="Create a TruXSocial account"
      data-testid="signup-cta-bar"
    >
      <Text $stacked={stacked}>
        New here? TruXSocial is a free-speech platform: five rules, nothing else.
      </Text>
      <Actions $stacked={stacked}>
        <SecondaryLink to={`/signin?next=${next}`} aria-label="Sign in">
          Sign in
        </SecondaryLink>
        <PrimaryLink to={`/signup?next=${next}`} aria-label="Create account">
          Create account
        </PrimaryLink>
      </Actions>
    </Bar>
  );
}
